// pages/components/settings/ProfessionalInfo.jsx
import React, { useState, useEffect } from 'react';
import { Save, Loader2, Check, Briefcase, Target, DollarSign, MapPin } from 'lucide-react';
import apiRequest from '@/services/requests';

const ProfessionalInfo = () => {
const [loading, setLoading] = useState(true);
const [saving, setSaving] = useState(false);
const [saved, setSaved] = useState(false);
const [formData, setFormData] = useState({
linkedin: '',
target_industry: '',
expected_salary_range: '',
target_region: [],
work_preference: '',
employment_type: [],
target_job_roles: [],
fluent_languages: [],
summary: '',
willing_to_relocate: false,
notice_period_days: null
});

useEffect(() => {
loadData();
}, []);

const loadData = async () => {
setLoading(true);
try {
const res = await apiRequest.getProfessionalInfo();
if (res.success) {
setFormData(res.data);
}
} catch (error) {
console.error('Error loading data:', error);
}
setLoading(false);
};

const handleInputChange = (e) => {
const { name, value, type, checked } = e.target;
setFormData(prev => ({
...prev,
[name]: type === 'checkbox' ? checked : value
}));
setSaved(false);
};

const handleMultiSelect = (name, value) => {
const currentValues = formData[name] || [];
const newValues = currentValues.includes(value)
? currentValues.filter(v => v !== value)
: [...currentValues, value];

setFormData(prev => ({ ...prev, [name]: newValues }));
setSaved(false);
};

const handleSubmit = async (e) => {
e.preventDefault();
setSaving(true);

try {
const res = await apiRequest.updateProfessionalInfo(formData);
if (res.success) {
setSaved(true);
setTimeout(() => setSaved(false), 3000);
}
} catch (error) {
console.error('Error saving:', error);
alert('Failed to save changes');
}

setSaving(false);
};

if (loading) {
return (
<div className="flex items-center justify-center py-12">
<Loader2 className="animate-spin text-indigo-600" size={32} />
</div>
);
}

const employmentTypes = ['full-time', 'part-time', 'contract', 'freelance'];
const workPreferences = ['remote', 'onsite', 'hybrid', 'flexible'];
const industries = [
'technology', 'finance', 'healthcare', 'education', 'marketing',
'sales', 'engineering', 'design', 'consulting', 'legal', 'other'
];
const languages = ['english', 'francais', 'spanish', 'german', 'mandarin', 'other'];

return (
<form onSubmit={handleSubmit} className="space-y-8">
<div>
<h2 className="hidden text-2xl font-bold text-slate-900 mb-6">Professional Information</h2>

{/* LinkedIn */}
<div className="mb-6">
<label className="block text-sm font-semibold text-slate-700 mb-2">
LinkedIn Profile URL
</label>
<input
type="url"
name="linkedin"
value={formData.linkedin || ''}
onChange={handleInputChange}
placeholder="https://linkedin.com/in/yourprofile"
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>

{/* Professional Summary */}
<div className="mb-6">
<label className="block text-sm font-semibold text-slate-700 mb-2">
Professional Summary
</label>
<textarea
name="summary"
value={formData.summary || ''}
onChange={handleInputChange}
rows={4}
placeholder="Brief description of your professional background and career goals..."
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
/>
</div>

{/* Target Industry & Job Roles */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Target Industry
</label>
<select
name="target_industry"
value={formData.target_industry || ''}
onChange={handleInputChange}
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 capitalize focus:border-transparent transition-all"
>
<option value="">Select Industry</option>
{industries.map(industry => (
<option key={industry} value={industry}>
{industry}
</option>
))}
</select>
</div>

<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Expected Salary Range
</label>
<input
type="text"
name="expected_salary_range"
value={formData.expected_salary_range || ''}
onChange={handleInputChange}
placeholder="e.g., $70,000 - $90,000"
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>
</div>

{/* Work Preference */}
<div className="mb-6">
<label className="block text-sm font-semibold text-slate-700 mb-3">
Work Preference
</label>
<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
{workPreferences.map(pref => (
<button
key={pref}
type="button"
onClick={() => handleInputChange({ 
target: { name: 'work_preference', value: pref } 
})}
className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
formData.work_preference === pref
? 'border-indigo-600 bg-indigo-50 text-indigo-700'
: 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
}`}
>
{pref.charAt(0).toUpperCase() + pref.slice(1)}
</button>
))}
</div>
</div>

{/* Employment Type (Multi-select) */}
<div className="mb-6">
<label className="block text-sm font-semibold text-slate-700 mb-3">
Employment Type Preferences (Select all that apply)
</label>
<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
{employmentTypes.map(type => {
const isSelected = (formData.employment_type || []).includes(type);
return (
<button
key={type}
type="button"
onClick={() => handleMultiSelect('employment_type', type)}
className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
isSelected
? 'border-indigo-600 bg-indigo-50 text-indigo-700'
: 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
}`}
>
{type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('-')}
</button>
);
})}
</div>
</div>

{/* Languages */}
<div className="mb-6">
<label className="block text-sm font-semibold text-slate-700 mb-3">
Fluent Languages (Select all that apply)
</label>
<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
{languages.map(lang => {
const isSelected = (formData.fluent_languages || []).includes(lang);
return (
<button
key={lang}
type="button"
onClick={() => handleMultiSelect('fluent_languages', lang)}
className={`px-4 py-3 capitalize rounded-lg border-2 font-medium transition-all ${
isSelected
? 'border-indigo-600 bg-indigo-50 text-indigo-700'
: 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
}`}
>
{lang}
</button>
);
})}
</div>
</div>

{/* Additional Info */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Notice Period (Days)
</label>
<input
type="number"
name="notice_period_days"
value={formData.notice_period_days || ''}
onChange={handleInputChange}
min="0"
placeholder="e.g., 30"
className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
</div>

<div className="flex items-center">
<label className="flex items-center gap-3 cursor-pointer">
<input
type="checkbox"
name="willing_to_relocate"
checked={formData.willing_to_relocate || false}
onChange={handleInputChange}
className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
/>
<span className="text-sm font-semibold text-slate-700">
Willing to Relocate
</span>
</label>
</div>
</div>
</div>

{/* Save Button */}
<div className="flex justify-end pt-6 border-t border-slate-200">
<button
type="submit"
disabled={saving}
className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-lg shadow-md transition-all"
>
{saving ? (
<>
<Loader2 className="animate-spin" size={20} />
Saving...
</>
) : saved ? (
<>
<Check size={20} />
Saved!
</>
) : (
<>
<Save size={20} />
Save Changes
</>
)}
</button>
</div>
</form>
);
};

export default ProfessionalInfo;